import {
  enhanceChartColorPickers
} from "../components/chart-color-picker.js";
import {
  enhanceChartDropdowns
} from "../components/chart-dropdown.js";
import {
  IndicatorDefinitionRepository,
  defaultInputs,
  defaultStyles,
  lineStyleOptions
} from "./indicator-definitions.js";
import {
  MAX_INDICATORS,
  MAX_INDICATOR_PANES,
  cloneIndicatorInstances,
  createIndicatorInstance,
  inputSummary,
  nextPaneId,
  normalizeIndicatorOrder,
  paneIds,
  paneLabel,
  resetIndicatorInstance,
  validateInput
} from "./indicator-model.js";

const DELETE_UNDO_DURATION = 6000;
const RECALCULATION_DEBOUNCE = 140;

export class IndicatorManager {
  constructor({
    element,
    repository = new IndicatorDefinitionRepository(),
    onApply,
    getAdditionError = null,
    onAddAlert = null,
    onDeleteIndicator = null,
    onAnnounce = null
  }) {
    if (!(element instanceof HTMLElement)) {
      throw new TypeError("IndicatorManager requires a dialog element.");
    }
    this.element = element;
    this.repository = repository;
    this.onApply = onApply;
    this.getAdditionError = getAdditionError;
    this.onAddAlert = onAddAlert;
    this.onDeleteIndicator = onDeleteIndicator;
    this.onAnnounce = onAnnounce;
    this.closeButton = required("[data-indicator-manager-close]", element);
    this.title = required("[data-indicator-manager-title]", element);
    this.symbol = required("[data-indicator-manager-symbol]", element);
    this.track = required("[data-indicator-manager-track]", element);
    this.manageView = required('[data-indicator-view="manage"]', element);
    this.configureView = required('[data-indicator-view="configure"]', element);
    this.search = required("[data-indicator-search]", element);
    this.clearSearch = required("[data-indicator-search-clear]", element);
    this.library = required("[data-indicator-library]", element);
    this.libraryStatus = required("[data-indicator-library-status]", element);
    this.panes = required("[data-indicator-panes]", element);
    this.activeCount = required("[data-indicator-active-count]", element);
    this.configureBody = required("[data-indicator-configure-body]", element);
    this.configureName = required("[data-indicator-configure-name]", element);
    this.configureBreadcrumb = required("[data-indicator-configure-breadcrumb]", element);
    this.configureBack = required("[data-indicator-configure-back]", element);
    this.configureCancel = required("[data-indicator-configure-cancel]", element);
    this.configureReset = required("[data-indicator-configure-reset]", element);
    this.configureDone = required("[data-indicator-configure-done]", element);
    this.manageFooter = required("[data-indicator-manage-footer]", element);
    this.configureFooter = required("[data-indicator-configure-footer]", element);
    this.cancelManaging = required("[data-indicator-manager-cancel]", element);
    this.doneManaging = required("[data-indicator-manager-done]", element);
    this.liveRegion = required("[data-indicator-manager-live]", element);
    this.undoToast = required("[data-indicator-undo]", element);
    this.undoMessage = required("[data-indicator-undo-message]", element);
    this.undoButton = required("[data-indicator-undo-action]", element);

    this.definitions = [];
    this.definitionMap = new Map();
    this.favorites = new Set();
    this.collapsedPanes = new Set();
    this.context = null;
    this.workingInstances = [];
    this.committedInstances = [];
    this.openingInstances = [];
    this.configureInstanceId = null;
    this.configureOriginal = null;
    this.dynamicDropdowns = [];
    this.dynamicColorPickers = [];
    this.recalculateTimer = 0;
    this.deleted = null;
    this.undoTimer = 0;
    this.opener = null;
    this.dragPayload = null;
    this.dragPreview = null;

    this.handleSearch = () => this.renderLibrary();
    this.handleKeydown = (event) => this.onKeydown(event);
    this.handleDocumentPointerDown = (event) => this.onDocumentPointerDown(event);
    this.handleBackdropPointerDown = (event) => {
      if (event.target === this.element) this.close();
    };
    this.handleLibraryClick = (event) => this.onLibraryClick(event);
    this.handleLibraryDragStart = (event) => this.onLibraryDragStart(event);
    this.handleLibraryDragEnd = () => this.onPanesDragEnd();
    this.handlePanesClick = (event) => this.onPanesClick(event);
    this.handlePanesDragStart = (event) => this.onPanesDragStart(event);
    this.handlePanesDragOver = (event) => this.onPanesDragOver(event);
    this.handlePanesDragLeave = (event) => this.onPanesDragLeave(event);
    this.handlePanesDrop = (event) => this.onPanesDrop(event);
    this.handlePanesDragEnd = () => this.onPanesDragEnd();
    this.handleConfigureInput = (event) => this.onConfigureInput(event);
    this.handleColorOpacity = (event) => this.onColorOpacity(event);

    this.search.addEventListener("input", this.handleSearch);
    this.clearSearch.addEventListener("click", () => {
      this.search.value = "";
      this.search.focus();
      this.renderLibrary();
    });
    this.closeButton.addEventListener("click", () => this.close());
    this.cancelManaging.addEventListener("click", () => this.cancelManagement());
    this.doneManaging.addEventListener("click", () => this.close());
    this.configureBack.addEventListener("click", () => this.cancelConfiguration());
    this.configureCancel.addEventListener("click", () => this.cancelConfiguration());
    this.configureReset.addEventListener("click", () => this.resetConfiguration());
    this.configureDone.addEventListener("click", () => this.commitConfiguration());
    this.undoButton.addEventListener("click", () => this.undoDelete());
    this.library.addEventListener("click", this.handleLibraryClick);
    this.library.addEventListener("dragstart", this.handleLibraryDragStart);
    this.library.addEventListener("dragend", this.handleLibraryDragEnd);
    this.panes.addEventListener("click", this.handlePanesClick);
    this.panes.addEventListener("dragstart", this.handlePanesDragStart);
    this.panes.addEventListener("dragover", this.handlePanesDragOver);
    this.panes.addEventListener("dragleave", this.handlePanesDragLeave);
    this.panes.addEventListener("drop", this.handlePanesDrop);
    this.panes.addEventListener("dragend", this.handlePanesDragEnd);
    this.configureView.addEventListener("input", this.handleConfigureInput);
    this.configureView.addEventListener("change", this.handleConfigureInput);
    this.configureView.addEventListener("chart-color-opacity", this.handleColorOpacity);
    this.configureView.addEventListener("click", (event) => this.onConfigureClick(event));
    this.element.addEventListener("keydown", this.handleKeydown);
    this.element.addEventListener("pointerdown", this.handleBackdropPointerDown);
    document.addEventListener("pointerdown", this.handleDocumentPointerDown);
    this.element.hidden = true;
  }

  async open({ target, symbol, instances, opener = document.activeElement }) {
    if (!target) return;
    this.context = { target, symbol };
    this.opener = opener instanceof HTMLElement ? opener : null;
    this.workingInstances = cloneIndicatorInstances(instances);
    this.committedInstances = cloneIndicatorInstances(instances);
    this.openingInstances = cloneIndicatorInstances(instances);
    this.configureInstanceId = null;
    this.configureOriginal = null;
    this.title.textContent = "Indicator Manager";
    this.symbol.textContent = symbol;
    this.showView("manage", false);
    this.element.hidden = false;
    this.element.dataset.state = "loading";
    this.libraryStatus.hidden = false;
    this.libraryStatus.textContent = "Loading indicators…";
    this.renderPanes();
    this.element.focus({ preventScroll: true });

    try {
      this.definitions = await this.repository.list();
      this.definitionMap = new Map(
        this.definitions.map((definition) => [definition.id, definition])
      );
      this.element.dataset.state = "ready";
      this.renderLibrary();
      this.renderPanes();
      this.search.focus({ preventScroll: true });
      this.announce("Indicator Manager opened.");
    } catch {
      this.element.dataset.state = "error";
      this.libraryStatus.hidden = false;
      this.libraryStatus.textContent = "Indicators could not be loaded. Close and try again.";
    }
  }

  close({ restoreFocus = true } = {}) {
    if (this.element.hidden) return;
    if (this.configureInstanceId) this.rollbackConfiguration();
    this.destroyDynamicControls();
    this.hideUndo();
    this.finishDragVisuals();
    this.dragPayload = null;
    this.element.hidden = true;
    this.element.dispatchEvent(new CustomEvent("indicator-manager-close"));
    this.context = null;
    this.configureInstanceId = null;
    this.configureOriginal = null;
    this.openingInstances = [];
    clearTimeout(this.recalculateTimer);
    if (restoreFocus) this.opener?.focus({ preventScroll: true });
    this.announce("Indicator Manager closed.");
  }

  replaceInstances(instances) {
    if (!this.context) return;
    this.workingInstances = cloneIndicatorInstances(instances);
    this.committedInstances = cloneIndicatorInstances(instances);
    this.configureInstanceId = null;
    this.configureOriginal = null;
    this.showView("manage", false);
    this.renderPanes();
  }

  destroy() {
    this.close({ restoreFocus: false });
    this.search.removeEventListener("input", this.handleSearch);
    this.library.removeEventListener("click", this.handleLibraryClick);
    this.library.removeEventListener("dragstart", this.handleLibraryDragStart);
    this.library.removeEventListener("dragend", this.handleLibraryDragEnd);
    this.panes.removeEventListener("click", this.handlePanesClick);
    this.panes.removeEventListener("dragstart", this.handlePanesDragStart);
    this.panes.removeEventListener("dragover", this.handlePanesDragOver);
    this.panes.removeEventListener("dragleave", this.handlePanesDragLeave);
    this.panes.removeEventListener("drop", this.handlePanesDrop);
    this.panes.removeEventListener("dragend", this.handlePanesDragEnd);
    this.configureView.removeEventListener("input", this.handleConfigureInput);
    this.configureView.removeEventListener("change", this.handleConfigureInput);
    this.configureView.removeEventListener("chart-color-opacity", this.handleColorOpacity);
    this.element.removeEventListener("keydown", this.handleKeydown);
    this.element.removeEventListener("pointerdown", this.handleBackdropPointerDown);
    document.removeEventListener("pointerdown", this.handleDocumentPointerDown);
  }

  renderLibrary() {
    if (this.definitions.length === 0) {
      this.library.replaceChildren();
      this.libraryStatus.hidden = false;
      this.libraryStatus.textContent = "No indicator definitions are available.";
      return;
    }
    const query = this.search.value.trim();
    this.clearSearch.hidden = query.length === 0;
    const matches = sortIndicatorLibraryDefinitions(
      this.repository.search(query),
      this.favorites
    );
    this.libraryStatus.hidden = true;
    this.library.replaceChildren();

    if (query && matches.length === 0) {
      this.libraryStatus.hidden = false;
      this.libraryStatus.innerHTML = `
        <strong>No indicators found</strong>
        <span>Try a name, category, description, or abbreviation.</span>
      `;
      return;
    }

    const results = document.createElement("div");
    results.className = "indicator-library-results";
    matches.forEach((definition) => results.append(this.createLibraryRow(definition)));
    this.library.append(results);
  }

  createLibraryRow(definition) {
    const additionError = this.additionError(definition.id);
    const row = document.createElement("article");
    row.className = "indicator-library-row";
    row.dataset.indicatorDefinition = definition.id;
    row.draggable = !additionError;
    row.tabIndex = additionError ? -1 : 0;
    row.setAttribute("aria-disabled", String(Boolean(additionError)));
    row.setAttribute("aria-label", `Add ${definition.name}`);
    row.title = additionError || `Add ${definition.name}`;
    row.innerHTML = `
      <div class="indicator-library-copy">
        <strong>${escapeHtml(definition.name)}</strong>
        <span>${escapeHtml(definition.abbreviation)} · ${escapeHtml(definition.description)}</span>
      </div>
      <button class="indicator-icon-button indicator-favorite"
              type="button"
              data-indicator-favorite="${definition.id}"
              aria-label="${this.favorites.has(definition.id) ? "Remove" : "Add"} ${escapeHtml(definition.name)} ${this.favorites.has(definition.id) ? "from" : "to"} favorites"
              aria-pressed="${this.favorites.has(definition.id)}"
              title="Favorite">
        ${icon("star")}
      </button>
    `;
    return row;
  }

  renderPanes() {
    this.panes.replaceChildren();
    this.activeCount.textContent = String(this.workingInstances.length);
    const ids = paneIds(this.workingInstances);
    for (const paneId of ids) {
      this.panes.append(this.createPaneGroup(paneId));
    }
    this.panes.append(this.createNewPaneZone());
  }

  createPaneGroup(paneId) {
    const paneInstances = this.workingInstances.filter(
      (instance) => instance.paneId === paneId
    );
    const section = document.createElement("section");
    const collapsed = this.collapsedPanes.has(paneId);
    section.className = "indicator-pane-group";
    section.dataset.indicatorPane = paneId;
    section.dataset.collapsed = String(collapsed);
    section.innerHTML = `
      <header class="indicator-pane-head">
        <button class="indicator-pane-collapse"
                type="button"
                data-indicator-pane-collapse="${paneId}"
                aria-label="${collapsed ? "Expand" : "Collapse"} ${escapeHtml(paneLabel(paneId, this.context?.symbol))}"
                aria-expanded="${!collapsed}">
          ${icon("chevron")}
        </button>
        <span class="indicator-drag-handle ${paneId === "main" ? "is-static" : ""}"
              ${paneId === "main"
                ? 'aria-hidden="true"'
                : `draggable="true"
                   data-indicator-pane-drag-handle="${paneId}"
                   aria-label="Drag ${escapeHtml(paneLabel(paneId, this.context?.symbol))} to reorder"
                   title="Drag to reorder pane"`}>
          ${icon("grip")}
        </span>
        <strong>${escapeHtml(paneLabel(paneId, this.context?.symbol))}</strong>
        <span class="indicator-pane-count">${paneInstances.length}</span>
        <details class="indicator-overflow">
          <summary aria-label="Actions for ${escapeHtml(paneLabel(paneId, this.context?.symbol))}">
            ${icon("more")}
          </summary>
          <div class="indicator-overflow-menu" role="menu">
            <button type="button" role="menuitem" data-indicator-pane-action="up" data-pane-id="${paneId}">
              Move pane up
            </button>
            <button type="button" role="menuitem" data-indicator-pane-action="down" data-pane-id="${paneId}">
              Move pane down
            </button>
          </div>
        </details>
      </header>
      <div class="indicator-pane-dropzone"
           data-indicator-drop-pane="${paneId}"
           aria-label="Drop indicators in ${escapeHtml(paneLabel(paneId, this.context?.symbol))}"></div>
    `;
    const dropzone = section.querySelector("[data-indicator-drop-pane]");
    if (collapsed) dropzone.hidden = true;
    paneInstances.forEach((instance) => dropzone.append(this.createActiveRow(instance)));
    if (paneInstances.length === 0) {
      const empty = document.createElement("p");
      empty.className = "indicator-pane-empty";
      empty.textContent = paneId === "main"
        ? "Drop an overlay indicator here."
        : "Drop an indicator here or remove this empty pane.";
      dropzone.append(empty);
    }
    return section;
  }

  createActiveRow(instance) {
    const definition = this.definitionMap.get(instance.definitionId);
    const row = document.createElement("article");
    row.className = "indicator-active-row";
    row.dataset.indicatorInstance = instance.id;
    row.draggable = true;
    row.tabIndex = 0;
    row.setAttribute("aria-label", `${definition?.name || instance.displayName} indicator`);
    row.innerHTML = `
      <span class="indicator-drag-handle" aria-hidden="true">${icon("grip")}</span>
      <button class="indicator-icon-button indicator-visibility"
              type="button"
              data-indicator-action="visibility"
              data-instance-id="${instance.id}"
              aria-label="${instance.visible ? "Hide" : "Show"} ${escapeHtml(definition?.name || instance.displayName)}"
              aria-pressed="${instance.visible}"
              title="${instance.visible ? "Hide" : "Show"}">
        ${icon(instance.visible ? "eye" : "eyeOff")}
      </button>
      <div class="indicator-active-copy">
        <strong>${escapeHtml(instance.displayName || definition?.abbreviation || "Unavailable")}</strong>
        <span>${escapeHtml(inputSummary(instance))}</span>
        ${instance.error ? `<small role="alert">${escapeHtml(instance.error)}</small>` : ""}
      </div>
      <button class="indicator-icon-button"
              type="button"
              data-indicator-action="settings"
              data-instance-id="${instance.id}"
              aria-label="Configure ${escapeHtml(definition?.name || instance.displayName)}"
              title="Settings">
        ${icon("settings")}
      </button>
      <button class="indicator-icon-button"
              type="button"
              data-indicator-action="alert"
              data-instance-id="${instance.id}"
              aria-label="Add alert to ${escapeHtml(definition?.name || instance.displayName)}"
              title="Add alert">
        ${icon("bell")}
      </button>
      <button class="indicator-icon-button indicator-delete"
              type="button"
              data-indicator-action="delete"
              data-instance-id="${instance.id}"
              aria-label="Delete ${escapeHtml(definition?.name || instance.displayName)}"
              title="Delete">
        ${icon("trash")}
      </button>
    `;
    return row;
  }

  createNewPaneZone() {
    const zone = document.createElement("button");
    zone.type = "button";
    zone.className = "indicator-new-pane-zone";
    zone.dataset.indicatorNewPane = "";
    const available = paneIds(this.workingInstances).length < MAX_INDICATOR_PANES;
    zone.disabled = !available;
    zone.innerHTML = `${icon("plus")}<span>${available ? "Drop here to create a new pane" : "Maximum panes reached"}</span>`;
    zone.setAttribute(
      "aria-label",
      available ? "Drop an indicator to create a new pane" : "Maximum indicator panes reached"
    );
    return zone;
  }

  onLibraryClick(event) {
    const favorite = closestButton(event.target, "[data-indicator-favorite]");
    if (favorite) {
      const id = favorite.dataset.indicatorFavorite;
      if (this.favorites.has(id)) this.favorites.delete(id);
      else this.favorites.add(id);
      this.renderLibrary();
      return;
    }
    const row = event.target instanceof Element
      ? event.target.closest("[data-indicator-definition]")
      : null;
    if (row instanceof HTMLElement) this.addIndicator(row.dataset.indicatorDefinition);
  }

  onLibraryDragStart(event) {
    const row = event.target instanceof Element
      ? event.target.closest("[data-indicator-definition]")
      : null;
    if (!(row instanceof HTMLElement)) return;
    const additionError = this.additionError(row.dataset.indicatorDefinition);
    if (additionError) {
      event.preventDefault();
      this.announce(additionError, true);
      return;
    }
    this.dragPayload = { type: "library", definitionId: row.dataset.indicatorDefinition };
    event.dataTransfer?.setData("application/x-flexcharting-indicator", JSON.stringify(this.dragPayload));
    if (event.dataTransfer) event.dataTransfer.effectAllowed = "copy";
    const definition = this.definitionMap.get(row.dataset.indicatorDefinition);
    this.beginDragVisuals(
      "library",
      row,
      event.dataTransfer,
      definition?.name || "Indicator",
      "Add to chart"
    );
  }

  onPanesDragStart(event) {
    const row = event.target instanceof Element
      ? event.target.closest("[data-indicator-instance]")
      : null;
    if (row instanceof HTMLElement) {
      this.dragPayload = { type: "instance", instanceId: row.dataset.indicatorInstance };
      event.dataTransfer?.setData("application/x-flexcharting-indicator", JSON.stringify(this.dragPayload));
      if (event.dataTransfer) event.dataTransfer.effectAllowed = "move";
      const instance = this.findInstance(row.dataset.indicatorInstance);
      this.beginDragVisuals(
        "instance",
        row,
        event.dataTransfer,
        instance?.displayName || "Indicator",
        paneLabel(instance?.paneId, this.context?.symbol)
      );
      return;
    }

    const paneHandle = event.target instanceof Element
      ? event.target.closest("[data-indicator-pane-drag-handle]")
      : null;
    const pane = paneHandle?.closest("[data-indicator-pane]");
    if (!(paneHandle instanceof HTMLElement) || !(pane instanceof HTMLElement)) return;
    const paneId = pane.dataset.indicatorPane;
    if (!paneId || paneId === "main") return;
    this.dragPayload = { type: "pane", paneId };
    event.dataTransfer?.setData("application/x-flexcharting-indicator", JSON.stringify(this.dragPayload));
    if (event.dataTransfer) event.dataTransfer.effectAllowed = "move";
    this.beginDragVisuals(
      "pane",
      pane,
      event.dataTransfer,
      paneLabel(paneId, this.context?.symbol),
      "Reorder pane"
    );
  }

  onPanesDragOver(event) {
    const target = this.resolveDropTarget(event.target);
    if (!target || !this.dragPayload) return;
    if (
      this.dragPayload.type === "pane"
      && target.dataset.indicatorPane === this.dragPayload.paneId
    ) {
      return;
    }
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = this.dragPayload.type === "library" ? "copy" : "move";
    }
    this.clearDropTargets();
    if (this.dragPayload.type === "pane") {
      const bounds = target.getBoundingClientRect();
      const insertAfter = target.dataset.indicatorPane === "main"
        || event.clientY > bounds.top + bounds.height / 2;
      target.classList.add("is-pane-drop-target");
      target.classList.toggle("is-insert-after", insertAfter);
      return;
    }
    target.classList.add("is-drop-target");
    if (target.matches("[data-indicator-instance]")) {
      const bounds = target.getBoundingClientRect();
      target.classList.toggle("is-insert-after", event.clientY > bounds.top + bounds.height / 2);
    }
  }

  onPanesDragLeave(event) {
    if (event.relatedTarget instanceof Node && this.panes.contains(event.relatedTarget)) return;
    this.clearDropTargets();
  }

  onPanesDrop(event) {
    const target = this.resolveDropTarget(event.target);
    if (!target) return;
    event.preventDefault();
    const payload = parseDragPayload(event.dataTransfer) || this.dragPayload;
    this.finishDragVisuals();
    this.dragPayload = null;
    if (!payload) return;

    if (payload.type === "pane") {
      const paneTarget = target.closest("[data-indicator-pane]");
      if (!(paneTarget instanceof HTMLElement)) return;
      const bounds = paneTarget.getBoundingClientRect();
      const insertAfter = paneTarget.dataset.indicatorPane === "main"
        || event.clientY > bounds.top + bounds.height / 2;
      this.movePane(
        payload.paneId,
        paneTarget.dataset.indicatorPane,
        insertAfter
      );
      return;
    }

    const newPane = target.matches("[data-indicator-new-pane]");
    const rowTarget = target.closest("[data-indicator-instance]");
    const paneTarget = target.closest("[data-indicator-drop-pane]");
    const paneId = newPane
      ? nextPaneId(this.workingInstances)
      : paneTarget?.dataset.indicatorDropPane
        || rowTarget?.closest("[data-indicator-pane]")?.dataset.indicatorPane;
    if (!paneId) {
      this.announce("The maximum number of panes has been reached.", true);
      return;
    }
    if (payload.type === "library") {
      this.addIndicator(payload.definitionId, paneId);
      return;
    }
    if (payload.type === "instance") {
      this.moveInstance(payload.instanceId, paneId, rowTarget, event.clientY);
    }
  }

  onPanesDragEnd() {
    this.dragPayload = null;
    this.finishDragVisuals();
  }

  resolveDropTarget(target) {
    if (!(target instanceof Element)) return null;
    if (this.dragPayload?.type === "pane") {
      return target.closest("[data-indicator-pane]");
    }
    return target.closest(
      "[data-indicator-instance], [data-indicator-drop-pane], [data-indicator-new-pane]"
    );
  }

  clearDropTargets() {
    this.panes.querySelectorAll(
      ".is-drop-target, .is-pane-drop-target, .is-insert-after"
    )
      .forEach((element) => element.classList.remove(
        "is-drop-target",
        "is-pane-drop-target",
        "is-insert-after"
      ));
  }

  beginDragVisuals(type, source, dataTransfer, title, detail) {
    this.finishDragVisuals();
    this.element.dataset.dragType = type;
    source.classList.add("is-dragging");
    const preview = document.createElement("div");
    preview.className = "indicator-drag-preview";
    preview.dataset.dragPreviewType = type;
    preview.innerHTML = `
      <span class="indicator-drag-preview-grip">${icon("grip")}</span>
      <span class="indicator-drag-preview-copy">
        <strong>${escapeHtml(title)}</strong>
        <span>${escapeHtml(detail)}</span>
      </span>
      <span class="indicator-drag-preview-badge">${type === "library" ? "Add" : "Move"}</span>
    `;
    document.body.append(preview);
    this.dragPreview = preview;
    if (dataTransfer && typeof dataTransfer.setDragImage === "function") {
      dataTransfer.setDragImage(preview, 18, 18);
    }
  }

  finishDragVisuals() {
    delete this.element.dataset.dragType;
    this.element.querySelectorAll(".is-dragging")
      .forEach((element) => element.classList.remove("is-dragging"));
    this.clearDropTargets();
    this.dragPreview?.remove();
    this.dragPreview = null;
  }

  onPanesClick(event) {
    const collapse = closestButton(event.target, "[data-indicator-pane-collapse]");
    if (collapse) {
      const paneId = collapse.dataset.indicatorPaneCollapse;
      if (this.collapsedPanes.has(paneId)) this.collapsedPanes.delete(paneId);
      else this.collapsedPanes.add(paneId);
      this.renderPanes();
      return;
    }
    const paneAction = closestButton(event.target, "[data-indicator-pane-action]");
    if (paneAction) {
      this.reorderPane(paneAction.dataset.paneId, paneAction.dataset.indicatorPaneAction);
      return;
    }
    const action = closestButton(event.target, "[data-indicator-action]");
    if (!action) return;
    const instanceId = action.dataset.instanceId;
    switch (action.dataset.indicatorAction) {
      case "visibility":
        this.toggleVisibility(instanceId);
        break;
      case "settings":
        this.openConfiguration(instanceId);
        break;
      case "alert": {
        const instance = this.workingInstances.find((item) => item.id === instanceId);
        const definition = instance
          ? this.definitionMap.get(instance.definitionId)
          : null;
        if (instance && definition && this.onAddAlert) {
          this.onAddAlert(this.context?.target, cloneIndicatorInstances([instance])[0], definition);
        }
        break;
      }
      case "delete":
        this.deleteInstance(instanceId);
        break;
      case "duplicate":
        this.duplicateInstance(instanceId);
        break;
      case "up":
      case "down":
        this.reorderInstance(instanceId, action.dataset.indicatorAction);
        break;
      case "move":
        this.moveInstance(instanceId, action.dataset.targetPane);
        break;
      case "new-pane":
        this.moveInstance(instanceId, nextPaneId(this.workingInstances));
        break;
      case "reset":
        this.resetInstance(instanceId);
        break;
      default:
        break;
    }
  }

  async addIndicator(definitionId, requestedPane = null) {
    const definition = this.definitionMap.get(definitionId);
    if (!definition) {
      this.announce("That indicator is no longer available.", true);
      return;
    }
    const additionError = this.additionError(definitionId);
    if (additionError) {
      this.announce(additionError, true);
      return;
    }
    if (this.workingInstances.length >= MAX_INDICATORS) {
      this.announce(`A chart can contain up to ${MAX_INDICATORS} indicators.`, true);
      return;
    }
    let paneId = requestedPane;
    if (!paneId) {
      paneId = definition.defaultPane === "main"
        ? "main"
        : nextPaneId(this.workingInstances)
          || paneIds(this.workingInstances).at(-1);
    }
    if (!paneId) {
      this.announce("The maximum number of panes has been reached.", true);
      return;
    }
    const instance = createIndicatorInstance(definition, { paneId });
    const next = normalizeIndicatorOrder([...this.workingInstances, instance]);
    await this.commitManagement(next, {
      recalculateIds: [instance.id],
      reason: `${definition.name} added to ${paneLabel(paneId, this.context?.symbol)}.`
    });
  }

  async toggleVisibility(instanceId) {
    const instance = this.findInstance(instanceId);
    if (!instance) return;
    instance.visible = !instance.visible;
    await this.commitManagement(this.workingInstances, {
      styleOnly: true,
      reason: `${instance.displayName} ${instance.visible ? "shown" : "hidden"}.`
    });
  }

  async duplicateInstance(instanceId) {
    const source = this.findInstance(instanceId);
    const definition = this.definitionMap.get(source?.definitionId);
    if (!source || !definition) return;
    const additionError = this.additionError(definition.id);
    if (additionError) {
      this.announce(additionError, true);
      return;
    }
    if (this.workingInstances.length >= MAX_INDICATORS) {
      this.announce(`A chart can contain up to ${MAX_INDICATORS} indicators.`, true);
      return;
    }
    const duplicate = createIndicatorInstance(definition, {
      paneId: source.paneId,
      scale: source.scale,
      visible: source.visible,
      inputs: structuredClone(source.inputs),
      styles: structuredClone(source.styles)
    });
    const sourceIndex = this.workingInstances.findIndex((candidate) => candidate.id === instanceId);
    const next = [...this.workingInstances];
    next.splice(sourceIndex + 1, 0, duplicate);
    await this.commitManagement(normalizeIndicatorOrder(next), {
      recalculateIds: [duplicate.id],
      reason: `${source.displayName} duplicated.`
    });
  }

  async resetInstance(instanceId) {
    const next = this.workingInstances.map((instance) => (
      instance.id === instanceId ? resetIndicatorInstance(instance) : instance
    ));
    await this.commitManagement(next, {
      recalculateIds: [instanceId],
      reason: "Indicator reset to defaults."
    });
  }

  async deleteInstance(instanceId) {
    const index = this.workingInstances.findIndex((instance) => instance.id === instanceId);
    if (index < 0) return;
    const removed = cloneIndicatorInstances([this.workingInstances[index]])[0];
    const next = this.workingInstances.filter((instance) => instance.id !== instanceId);
    this.deleted = { instance: removed, index };
    await this.commitManagement(normalizeIndicatorOrder(next), {
      styleOnly: true,
      reason: `${removed.displayName} deleted.`
    });
    await this.onDeleteIndicator?.(this.context?.target, removed);
    this.showUndo(`${removed.displayName} deleted`);
  }

  async undoDelete() {
    if (!this.deleted) return;
    const { instance, index } = this.deleted;
    const next = [...this.workingInstances];
    next.splice(Math.min(index, next.length), 0, instance);
    this.hideUndo();
    await this.commitManagement(normalizeIndicatorOrder(next), {
      recalculateIds: [instance.id],
      reason: `${instance.displayName} restored.`
    });
  }

  async reorderInstance(instanceId, direction) {
    const index = this.workingInstances.findIndex((instance) => instance.id === instanceId);
    if (index < 0) return;
    const paneId = this.workingInstances[index].paneId;
    const paneIndices = this.workingInstances
      .map((instance, candidateIndex) => ({ instance, candidateIndex }))
      .filter(({ instance }) => instance.paneId === paneId)
      .map(({ candidateIndex }) => candidateIndex);
    const position = paneIndices.indexOf(index);
    const targetPosition = direction === "up" ? position - 1 : position + 1;
    if (targetPosition < 0 || targetPosition >= paneIndices.length) return;
    const targetIndex = paneIndices[targetPosition];
    const next = [...this.workingInstances];
    [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
    await this.commitManagement(normalizeIndicatorOrder(next), {
      styleOnly: true,
      reason: `${next[targetIndex].displayName} moved ${direction}.`
    });
  }

  async reorderPane(paneId, direction) {
    if (paneId === "main") return;
    const ids = paneIds(this.workingInstances);
    const index = ids.indexOf(paneId);
    const target = direction === "up" ? index - 1 : index + 1;
    if (target <= 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target], ids[index]];
    const next = ids.flatMap((id) => (
      this.workingInstances.filter((instance) => instance.paneId === id)
    ));
    await this.commitManagement(normalizeIndicatorOrder(next), {
      styleOnly: true,
      reason: `${paneLabel(paneId)} moved ${direction}.`
    });
  }

  async movePane(paneId, targetPaneId, insertAfter) {
    if (
      !paneId
      || paneId === "main"
      || !targetPaneId
      || paneId === targetPaneId
    ) {
      return;
    }
    const ids = paneIds(this.workingInstances);
    if (!ids.includes(paneId) || !ids.includes(targetPaneId)) return;
    const nextIds = ids.filter((id) => id !== paneId);
    const targetIndex = nextIds.indexOf(targetPaneId);
    const insertIndex = targetPaneId === "main"
      ? 1
      : targetIndex + (insertAfter ? 1 : 0);
    nextIds.splice(insertIndex, 0, paneId);
    if (nextIds.every((id, index) => id === ids[index])) return;
    const next = nextIds.flatMap((id) => (
      this.workingInstances.filter((instance) => instance.paneId === id)
    ));
    await this.commitManagement(normalizeIndicatorOrder(next), {
      styleOnly: true,
      reason: `${paneLabel(paneId)} moved.`
    });
  }

  async moveInstance(instanceId, paneId, rowTarget = null, pointerY = null) {
    if (!paneId) {
      this.announce("The maximum number of panes has been reached.", true);
      return;
    }
    const sourceIndex = this.workingInstances.findIndex((instance) => instance.id === instanceId);
    if (sourceIndex < 0) return;
    const moving = { ...this.workingInstances[sourceIndex], paneId };
    const next = this.workingInstances.filter((instance) => instance.id !== instanceId);
    let insertIndex = next.length;
    if (rowTarget instanceof HTMLElement) {
      const rowId = rowTarget.dataset.indicatorInstance;
      insertIndex = next.findIndex((instance) => instance.id === rowId);
      if (insertIndex < 0) insertIndex = next.length;
      if (Number.isFinite(pointerY)) {
        const bounds = rowTarget.getBoundingClientRect();
        if (pointerY > bounds.top + bounds.height / 2) insertIndex += 1;
      }
    } else {
      const lastPaneIndex = next.findLastIndex((instance) => instance.paneId === paneId);
      if (lastPaneIndex >= 0) insertIndex = lastPaneIndex + 1;
    }
    next.splice(insertIndex, 0, moving);
    await this.commitManagement(normalizeIndicatorOrder(next), {
      styleOnly: true,
      reason: `${moving.displayName} moved to ${paneLabel(paneId, this.context?.symbol)}.`
    });
  }

  openConfiguration(instanceId) {
    const instance = this.findInstance(instanceId);
    const definition = this.definitionMap.get(instance?.definitionId);
    if (!instance || !definition) {
      this.announce("This indicator cannot be configured because its definition is unavailable.", true);
      return;
    }
    this.configureInstanceId = instanceId;
    this.configureOriginal = cloneIndicatorInstances([instance])[0];
    this.configureName.textContent = definition.name;
    this.configureBreadcrumb.textContent = `Indicators / ${definition.abbreviation}`;
    this.renderConfiguration();
    this.showView("configure", true);
    this.configureBack.focus({ preventScroll: true });
  }

  renderConfiguration() {
    const instance = this.findInstance(this.configureInstanceId);
    const definition = this.definitionMap.get(instance?.definitionId);
    if (!instance || !definition) return;
    this.destroyDynamicControls();
    this.configureBody.innerHTML = `
      <div class="indicator-configure-panels">
        <section class="indicator-configure-panel" aria-labelledby="indicator-inputs-heading">
          <header>
            <h3 id="indicator-inputs-heading">Inputs</h3>
            <span>Calculation parameters</span>
          </header>
          <div class="indicator-configure-fields">
            ${definition.inputs.map((input) => this.renderInputControl(input, instance)).join("")}
          </div>
        </section>
        <section class="indicator-configure-panel" aria-labelledby="indicator-style-heading">
          <header>
            <h3 id="indicator-style-heading">Style</h3>
            <span>Outputs and labels</span>
          </header>
          <div class="indicator-output-list">
            ${definition.outputs.map((output) => this.renderOutputControl(output, instance)).join("")}
          </div>
        </section>
      </div>
    `;
    this.dynamicDropdowns = enhanceChartDropdowns(this.configureBody);
    this.dynamicColorPickers = enhanceChartColorPickers(this.configureBody);
  }

  renderInputControl(input, instance) {
    const value = instance.inputs[input.key];
    if (input.type === "boolean") {
      return `
        <label class="indicator-reference-option indicator-input-toggle">
          <input type="checkbox"
                 data-indicator-input="${input.key}"
                 ${value === true ? "checked" : ""} />
          <span>${escapeHtml(input.label)}</span>
        </label>
      `;
    }
    const control = ["select", "source"].includes(input.type)
      ? `
        <select data-indicator-input="${input.key}"
                data-chart-dropdown
                aria-label="${escapeHtml(input.label)}">
          ${(input.options || []).map((option) => `
            <option value="${escapeHtml(option.value)}" ${option.value === value ? "selected" : ""}>
              ${escapeHtml(option.label)}
            </option>
          `).join("")}
        </select>
      `
      : input.type === "color"
        ? `
          <input type="color"
                 value="${escapeHtml(value)}"
                 data-indicator-input="${input.key}"
                 data-chart-color-picker
                 aria-label="${escapeHtml(input.label)}" />
        `
        : `
          <input type="number"
                 value="${escapeHtml(value)}"
                 min="${input.minimum}"
                 max="${input.maximum}"
                 step="${input.step || 1}"
                 data-indicator-input="${input.key}"
                 aria-label="${escapeHtml(input.label)}" />
        `;
    return `
      <label class="indicator-configure-field" data-indicator-input-field="${input.key}">
        <span>
          ${escapeHtml(input.label)}
          <button type="button"
                  class="indicator-input-reset"
                  data-indicator-input-reset="${input.key}"
                  aria-label="Reset ${escapeHtml(input.label)}"
                  title="Reset input">
            ${icon("reset")}
          </button>
        </span>
        ${control}
        ${input.help ? `<small>${escapeHtml(input.help)}</small>` : ""}
        <small class="indicator-input-error" data-indicator-input-error="${input.key}" role="alert"></small>
      </label>
    `;
  }

  renderOutputControl(output, instance) {
    const style = instance.styles[output.key] || output.defaultStyle;
    const colorControl = `
      <label class="indicator-output-color">
        <span>Color</span>
        <input type="color"
               value="${escapeHtml(style.color)}"
               data-indicator-style="${output.key}.color"
               data-chart-opacity="${Number(style.opacity)}"
               data-chart-opacity-setting="${output.key}.opacity"
               data-chart-color-picker
               aria-label="${escapeHtml(output.label)} color" />
      </label>
    `;
    const lineControls = output.kind !== "fill"
      ? `
        <div class="indicator-configure-field indicator-width-field">
          <span>Width</span>
          <div class="indicator-width-options"
               role="radiogroup"
               aria-label="${escapeHtml(output.label)} line width">
            ${[1, 1.5, 2, 3, 4].map((width) => `
              <label class="indicator-width-option"
                     title="${width} ${width === 1 ? "pixel" : "pixels"}">
                <input type="radio"
                       name="indicator-width-${escapeHtml(instance.id)}-${escapeHtml(output.key)}"
                       value="${width}"
                       data-indicator-style="${output.key}.width"
                       aria-label="${width} ${width === 1 ? "pixel" : "pixels"}"
                       ${Number(style.width) === width ? "checked" : ""} />
                <span class="indicator-width-sample"
                      style="--indicator-width-sample: ${width}px"
                      aria-hidden="true"></span>
              </label>
            `).join("")}
          </div>
        </div>
        <label class="indicator-configure-field">
          <span>Line style</span>
          <select data-indicator-style="${output.key}.lineStyle" data-chart-dropdown aria-label="${escapeHtml(output.label)} line style">
            ${lineStyleOptions().map((option) => `
              <option value="${option.value}" ${style.lineStyle === option.value ? "selected" : ""}>${option.label}</option>
            `).join("")}
          </select>
        </label>
      `
      : "";
    return `
      <fieldset class="indicator-output-card">
        <legend>${escapeHtml(output.label)}</legend>
        <div class="indicator-output-head">
          <label class="indicator-toggle-option indicator-output-toggle">
            <span>${escapeHtml(output.label)}</span>
            <input type="checkbox"
                   data-indicator-style="${output.key}.visible"
                   ${style.visible !== false ? "checked" : ""} />
            <i class="indicator-toggle-track" aria-hidden="true"></i>
          </label>
          ${colorControl}
        </div>
        <div class="indicator-output-grid">
          ${lineControls}
          <label class="indicator-toggle-option">
            <span>Price-scale label</span>
            <input type="checkbox"
                   data-indicator-style="${output.key}.priceLabel"
                   ${style.priceLabel === true ? "checked" : ""} />
            <i class="indicator-toggle-track" aria-hidden="true"></i>
          </label>
          <label class="indicator-toggle-option">
            <span>Data window label</span>
            <input type="checkbox"
                   data-indicator-style="${output.key}.lastValueLabel"
                   ${style.lastValueLabel === true ? "checked" : ""} />
            <i class="indicator-toggle-track" aria-hidden="true"></i>
          </label>
        </div>
      </fieldset>
    `;
  }

  onConfigureClick(event) {
    const reset = closestButton(event.target, "[data-indicator-input-reset]");
    if (!reset) return;
    const instance = this.findInstance(this.configureInstanceId);
    const definition = this.definitionMap.get(instance?.definitionId);
    const input = definition?.inputs.find(
      (candidate) => candidate.key === reset.dataset.indicatorInputReset
    );
    if (!instance || !input) return;
    instance.inputs[input.key] = structuredClone(input.defaultValue);
    this.renderConfiguration();
    this.scheduleInputPreview(instance.id);
    this.announce(`${input.label} reset.`);
  }

  onConfigureInput(event) {
    const control = event.target;
    if (!(control instanceof HTMLInputElement || control instanceof HTMLSelectElement)) return;
    const instance = this.findInstance(this.configureInstanceId);
    const definition = this.definitionMap.get(instance?.definitionId);
    if (!instance || !definition) return;

    const inputKey = control.dataset.indicatorInput;
    if (inputKey) {
      const rawValue = control.type === "checkbox" ? control.checked : control.value;
      const validation = validateInput(definition, inputKey, rawValue, instance.inputs);
      const error = this.configureBody.querySelector(`[data-indicator-input-error="${inputKey}"]`);
      const field = this.configureBody.querySelector(`[data-indicator-input-field="${inputKey}"]`);
      control.setAttribute("aria-invalid", String(!validation.valid));
      field?.classList.toggle("has-error", !validation.valid);
      if (error) error.textContent = validation.valid ? "" : validation.message;
      this.configureDone.disabled = !validation.valid
        || this.configureBody.querySelector('[aria-invalid="true"]') !== null;
      if (!validation.valid) return;
      instance.inputs[inputKey] = validation.value;
      this.scheduleInputPreview(instance.id);
      return;
    }
    const stylePath = control.dataset.indicatorStyle;
    if (stylePath) {
      const [outputKey, property] = stylePath.split(".");
      instance.styles[outputKey] ||= {};
      instance.styles[outputKey][property] = control.type === "checkbox"
        ? control.checked
        : ["width", "opacity"].includes(property)
          ? Number(control.value)
          : control.value;
      if (property === "opacity") {
        control.closest("label")?.querySelector("output")?.replaceChildren(`${control.value}%`);
      }
      this.applyWorking({ styleOnly: true });
    }
  }

  onColorOpacity(event) {
    const control = event.target;
    if (!(control instanceof HTMLInputElement)) return;
    const stylePath = control.dataset.chartOpacitySetting;
    const opacity = Number(event.detail?.opacity);
    const instance = this.findInstance(this.configureInstanceId);
    if (!stylePath || !Number.isFinite(opacity) || !instance) return;
    const [outputKey, property] = stylePath.split(".");
    instance.styles[outputKey] ||= {};
    instance.styles[outputKey][property] = opacity;
    this.applyWorking({ styleOnly: true });
  }

  scheduleInputPreview(instanceId) {
    clearTimeout(this.recalculateTimer);
    this.recalculateTimer = window.setTimeout(() => {
      this.applyWorking({ recalculateIds: [instanceId] });
    }, RECALCULATION_DEBOUNCE);
  }

  resetConfiguration() {
    const index = this.workingInstances.findIndex(
      (instance) => instance.id === this.configureInstanceId
    );
    if (index < 0) return;
    this.workingInstances[index] = resetIndicatorInstance(this.workingInstances[index]);
    this.renderConfiguration();
    this.applyWorking({ recalculateIds: [this.configureInstanceId] });
    this.announce("Indicator configuration reset to defaults.");
  }

  async commitConfiguration() {
    if (!this.configureInstanceId || this.configureDone.disabled) return;
    clearTimeout(this.recalculateTimer);
    await this.applyWorking({ recalculateIds: [this.configureInstanceId], commit: true });
    this.committedInstances = cloneIndicatorInstances(this.workingInstances);
    const name = this.findInstance(this.configureInstanceId)?.displayName || "Indicator";
    this.configureInstanceId = null;
    this.configureOriginal = null;
    this.renderPanes();
    this.showView("manage", true);
    this.announce(`${name} settings saved.`);
  }

  cancelConfiguration() {
    if (!this.configureInstanceId) return;
    this.rollbackConfiguration();
    this.configureInstanceId = null;
    this.configureOriginal = null;
    this.renderPanes();
    this.showView("manage", true);
    this.configureName.textContent = "";
    this.announce("Indicator changes canceled.");
  }

  rollbackConfiguration() {
    clearTimeout(this.recalculateTimer);
    this.workingInstances = cloneIndicatorInstances(this.committedInstances);
    this.applyWorking({ styleOnly: true, rollback: true });
  }

  async commitManagement(next, options = {}) {
    const previousWorking = cloneIndicatorInstances(this.workingInstances);
    const previousCommitted = cloneIndicatorInstances(this.committedInstances);
    this.workingInstances = cloneIndicatorInstances(next);
    this.committedInstances = cloneIndicatorInstances(next);
    const applied = await this.applyWorking({ ...options, commit: true });
    if (!applied) {
      this.workingInstances = previousWorking;
      this.committedInstances = previousCommitted;
      this.renderPanes();
      this.renderLibrary();
      return false;
    }
    this.renderPanes();
    this.renderLibrary();
    if (options.reason) this.announce(options.reason);
    return true;
  }

  async cancelManagement() {
    if (!this.context) return;
    clearTimeout(this.recalculateTimer);
    const changedWorking = cloneIndicatorInstances(this.workingInstances);
    const changedCommitted = cloneIndicatorInstances(this.committedInstances);
    this.workingInstances = cloneIndicatorInstances(this.openingInstances);
    this.committedInstances = cloneIndicatorInstances(this.openingInstances);
    const restored = await this.applyWorking({ commit: true, rollback: true });
    if (!restored) {
      this.workingInstances = changedWorking;
      this.committedInstances = changedCommitted;
      this.renderPanes();
      return;
    }
    this.close();
    this.announce("Indicator changes canceled.");
  }

  async applyWorking(options = {}) {
    if (!this.context || typeof this.onApply !== "function") return false;
    try {
      await this.onApply(
        this.context.target,
        cloneIndicatorInstances(this.workingInstances),
        options
      );
      return true;
    } catch (error) {
      this.announce(
        error instanceof Error ? error.message : "The indicator update could not be applied.",
        true
      );
      return false;
    }
  }

  additionError(definitionId) {
    if (typeof this.getAdditionError !== "function") return "";
    const result = this.getAdditionError(
      definitionId,
      cloneIndicatorInstances(this.workingInstances)
    );
    return typeof result === "string" ? result : "";
  }

  showView(name, animate) {
    this.element.dataset.view = name;
    this.track.classList.toggle("show-configure", name === "configure");
    this.track.classList.toggle("without-transition", !animate);
    this.manageView.setAttribute("aria-hidden", String(name !== "manage"));
    this.configureView.setAttribute("aria-hidden", String(name !== "configure"));
    this.manageFooter.hidden = name !== "manage";
    this.configureFooter.hidden = name !== "configure";
    requestAnimationFrame(() => this.track.classList.remove("without-transition"));
  }

  showUndo(message) {
    clearTimeout(this.undoTimer);
    this.undoMessage.textContent = message;
    this.undoToast.hidden = false;
    this.undoTimer = window.setTimeout(() => this.hideUndo(), DELETE_UNDO_DURATION);
  }

  hideUndo() {
    clearTimeout(this.undoTimer);
    this.undoToast.hidden = true;
    this.deleted = null;
  }

  findInstance(id) {
    return this.workingInstances.find((instance) => instance.id === id) || null;
  }

  onKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      if (this.configureInstanceId) this.cancelConfiguration();
      else this.close();
      return;
    }
    if (
      ["Enter", " "].includes(event.key)
      && event.target instanceof HTMLElement
      && event.target.matches("[data-indicator-definition]")
    ) {
      event.preventDefault();
      this.addIndicator(event.target.dataset.indicatorDefinition);
      return;
    }
    if (event.key !== "Tab") return;
    const focusable = [...this.element.querySelectorAll(
      'button:not([disabled]):not([hidden]), input:not([disabled]):not([hidden]), '
      + 'select:not([disabled]):not([hidden]), summary, [tabindex]:not([tabindex="-1"])'
    )].filter((candidate) => candidate instanceof HTMLElement && candidate.offsetParent !== null);
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable.at(-1);
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  onDocumentPointerDown(event) {
    if (this.element.hidden || !(event.target instanceof Node)) return;
    const bounds = this.element.getBoundingClientRect();
    const pointerIsInsidePanel = event.clientX >= bounds.left
      && event.clientX <= bounds.right
      && event.clientY >= bounds.top
      && event.clientY <= bounds.bottom;
    if (pointerIsInsidePanel && this.element.contains(event.target)) return;
    this.close();
  }

  announce(message, assertive = false) {
    this.liveRegion.setAttribute("aria-live", assertive ? "assertive" : "polite");
    this.liveRegion.textContent = "";
    requestAnimationFrame(() => {
      this.liveRegion.textContent = message;
      this.onAnnounce?.(message);
    });
  }

  destroyDynamicControls() {
    for (const dropdown of this.dynamicDropdowns) dropdown.destroy();
    for (const colorPicker of this.dynamicColorPickers) colorPicker.destroy();
    this.dynamicDropdowns = [];
    this.dynamicColorPickers = [];
  }
}

export function sortIndicatorLibraryDefinitions(definitions, favoriteIds = new Set()) {
  return definitions
    .filter((definition) => definition.category === "Built-in indicators")
    .sort((left, right) => {
      const favoriteOrder =
        Number(!favoriteIds.has(left.id)) - Number(!favoriteIds.has(right.id));
      if (favoriteOrder !== 0) return favoriteOrder;
      return left.name.localeCompare(right.name, undefined, { sensitivity: "base" });
    });
}

function required(selector, root) {
  const element = root.querySelector(selector);
  if (!(element instanceof HTMLElement)) {
    throw new Error(`Indicator Manager element ${selector} was not found.`);
  }
  return element;
}

function closestButton(target, selector) {
  if (!(target instanceof Element)) return null;
  const button = target.closest(selector);
  return button instanceof HTMLButtonElement ? button : null;
}

function parseDragPayload(dataTransfer) {
  const raw = dataTransfer?.getData("application/x-flexcharting-indicator");
  if (!raw) return null;
  try {
    const payload = JSON.parse(raw);
    return payload?.type ? payload : null;
  } catch {
    return null;
  }
}

function icon(name) {
  const paths = {
    plus: '<path d="M12 5v14M5 12h14"></path>',
    star: '<path d="m12 3 2.7 5.5 6.1.9-4.4 4.3 1 6.1-5.4-2.9-5.4 2.9 1-6.1-4.4-4.3 6.1-.9Z"></path>',
    grip: '<circle cx="9" cy="7" r="1"></circle><circle cx="15" cy="7" r="1"></circle><circle cx="9" cy="12" r="1"></circle><circle cx="15" cy="12" r="1"></circle><circle cx="9" cy="17" r="1"></circle><circle cx="15" cy="17" r="1"></circle>',
    eye: '<path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z"></path><circle cx="12" cy="12" r="2.5"></circle>',
    eyeOff: '<path d="m3 3 18 18M10.6 6.2A10 10 0 0 1 12 6c6 0 9.5 6 9.5 6a15 15 0 0 1-2.1 2.8M6.6 6.6C4 8.3 2.5 12 2.5 12s3.5 6 9.5 6a9.5 9.5 0 0 0 3-.5"></path>',
    settings: '<path d="M4 7h10M18 7h2M4 17h2M10 17h10"></path><circle cx="16" cy="7" r="2"></circle><circle cx="8" cy="17" r="2"></circle>',
    bell: '<path d="M6 17h12l-1.5-2.5V10a4.5 4.5 0 0 0-9 0v4.5L6 17Z"></path><path d="M10 20h4"></path>',
    trash: '<path d="M4 7h16M9 7V4h6v3M7 7l1 13h8l1-13M10 11v5M14 11v5"></path>',
    more: '<circle cx="5" cy="12" r="1"></circle><circle cx="12" cy="12" r="1"></circle><circle cx="19" cy="12" r="1"></circle>',
    chevron: '<path d="m7 10 5 5 5-5"></path>',
    reset: '<path d="M3 12a9 9 0 1 0 3-6.7L3 8"></path><path d="M3 3v5h5"></path>'
  };
  return `<svg viewBox="0 0 24 24" aria-hidden="true">${paths[name] || ""}</svg>`;
}

function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}
